import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;

const Shell = ({ icon, title, text, testid }) => (
  <section data-testid={testid} className="min-h-[60vh] flex items-center justify-center px-6 py-20 bg-white">
    <div className="max-w-lg text-center">
      <div className="flex justify-center mb-6">{icon}</div>
      <h1 className="font-head text-4xl sm:text-5xl uppercase text-[#141414] mb-4">{title}</h1>
      <p className="text-gray-600 mb-8">{text}</p>
      <Link to="/shop" data-testid="payment-back-to-shop" className="btn-crimson inline-block">Back to Shop</Link>
    </div>
  </section>
);

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clearCart } = useCart();
  const [state, setState] = useState(cancelled ? 'cancelled' : 'checking');

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) { setState('error'); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') { clearCart(); setState('paid'); return; }
        if (data.status === 'expired' || data.payment_status === 'failed') { setState('error'); return; }
      } catch { /* keep polling */ }
      if (++tries >= MAX_POLLS) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'checking') return <Shell testid="payment-checking" icon={<Loader2 size={56} className="animate-spin text-[#c8102e]" />} title="Confirming Payment" text="Hang tight while we confirm your payment with Stripe." />;
  if (state === 'paid') return <Shell testid="payment-success" icon={<CheckCircle2 size={56} className="text-green-600" />} title="Order Confirmed" text="Thank you for your order. A receipt has been sent to your email and we'll be in touch once it ships." />;
  if (state === 'cancelled') return <Shell testid="payment-cancelled" icon={<XCircle size={56} className="text-[#c8102e]" />} title="Checkout Cancelled" text="No payment was taken. Your cart is still saved whenever you're ready." />;
  if (state === 'timeout') return <Shell testid="payment-timeout" icon={<Loader2 size={56} className="text-[#c8102e]" />} title="Still Processing" text="We couldn't confirm your payment yet. Please check your email for a receipt or contact us." />;
  return <Shell testid="payment-error" icon={<XCircle size={56} className="text-[#c8102e]" />} title="Payment Not Completed" text="Your payment didn't go through. Your cart is still saved — please try again." />;
}
